/**
 * V2 DataTable — a small generic TanStack Table wrapper.
 *
 * Renders the `.tbl` treatment from design_reference/styles/app.css:
 * sticky header (bg-1), `--row-h` rows, hairline borders, hover row highlight,
 * sortable column headers (accent-ink caret on the sorted column), and
 * hover-revealed, right-aligned row actions.
 *
 * Generic over the row type so callers get type-safe columns/data.
 *
 * PRP-WEB-1 W2.2: an optional default sort (`initialSorting`), a sort remembered per browser
 * (`sortStorageKey`) and caller-owned column visibility (`columnVisibility`, see ColumnsMenu).
 */

import type { ReactNode } from 'react';
import { useState } from 'react';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { cn } from '../../lib/utils';
import { IconChevd } from './icons';
import { Skeleton } from './ui/skeleton';

/** Props for the generic V2 DataTable. */
export interface DataTableProps<TRow> {
  columns: ColumnDef<TRow, unknown>[];
  data: TRow[];
  /** Invoked when a body row is clicked (e.g. navigate to detail). */
  onRowClick?: (_row: TRow) => void;
  /**
   * Optional per-row actions, rendered right-aligned and revealed on row hover.
   * Use `e.stopPropagation()` inside handlers so actions don't trigger row click.
   */
  rowActions?: (_row: TRow) => ReactNode;
  /** The sort before the user picks one (and when nothing is remembered). */
  initialSorting?: SortingState;
  /** localStorage key that remembers the user's sort in this browser. */
  sortStorageKey?: string;
  /** Hidden columns map to false (TanStack column visibility). */
  columnVisibility?: VisibilityState;
}

/** Reads a JSON value from localStorage; null when absent, unreadable or blocked. */
export function readStored<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

/** Writes a JSON value to localStorage; a blocked or full store is ignored. */
export function writeStored(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // private window / blocked storage: the choice lasts for this visit only
  }
}

/** A reusable, sortable, sticky-header data table themed to V2 tokens. */
export function DataTable<TRow>({
  columns,
  data,
  onRowClick,
  rowActions,
  initialSorting,
  sortStorageKey,
  columnVisibility,
}: DataTableProps<TRow>) {
  const [sorting, setSorting] = useState<SortingState>(() => {
    const stored = sortStorageKey ? readStored<SortingState>(sortStorageKey) : null;
    return Array.isArray(stored) ? stored : (initialSorting ?? []);
  });

  const table = useReactTable({
    data,
    columns,
    state: { sorting, ...(columnVisibility ? { columnVisibility } : {}) },
    onSortingChange: (updater) => {
      setSorting((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        if (sortStorageKey) writeStored(sortStorageKey, next);
        return next;
      });
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="overflow-auto rounded-[var(--r-lg)] border border-[var(--bd-subtle)] bg-[var(--bg-2)]">
      <table className="w-full border-collapse text-[12.5px]">
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((header) => {
                const canSort = header.column.getCanSort();
                const sorted = header.column.getIsSorted();
                const align = (header.column.columnDef.meta as { align?: 'right' } | undefined)
                  ?.align;
                return (
                  <th
                    key={header.id}
                    aria-sort={sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : undefined}
                    onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                    className={cn(
                      'sticky top-0 z-[2] h-9 whitespace-nowrap border-b border-[var(--bd)] bg-[var(--bg-1)] px-3',
                      'text-[11px] font-semibold uppercase tracking-[.02em] text-[var(--tx-lo)] select-none',
                      align === 'right' ? 'text-right' : 'text-left',
                      canSort && 'cursor-pointer',
                      sorted && 'text-[var(--tx-hi)]',
                    )}
                  >
                    <span
                      className={cn(
                        'inline-flex items-center gap-[5px]',
                        align === 'right' && 'flex-row-reverse',
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                      {canSort && (
                        <IconChevd
                          className={cn(
                            'h-[13px] w-[13px] transition-transform',
                            sorted ? 'text-[var(--accent-ink)] opacity-100' : 'opacity-50',
                            sorted === 'asc' && 'rotate-180',
                          )}
                        />
                      )}
                    </span>
                  </th>
                );
              })}
              {rowActions && <th className="sticky top-0 z-[2] h-9 w-[110px] border-b border-[var(--bd)] bg-[var(--bg-1)] px-3" />}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              onClick={onRowClick ? () => onRowClick(row.original) : undefined}
              className={cn(
                'group border-b border-[var(--bd-subtle)] hover:bg-[var(--bg-3)]',
                onRowClick && 'cursor-pointer',
              )}
            >
              {row.getVisibleCells().map((cell) => {
                const align = (cell.column.columnDef.meta as { align?: 'right' } | undefined)
                  ?.align;
                return (
                  <td
                    key={cell.id}
                    className={cn(
                      'h-[var(--row-h)] px-3 align-middle text-[var(--tx-mid)]',
                      align === 'right' && 'text-right',
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                );
              })}
              {rowActions && (
                <td className="h-[var(--row-h)] px-3 align-middle text-right">
                  <span className="inline-flex gap-[2px] opacity-0 transition-opacity group-hover:opacity-100">
                    {rowActions(row.original)}
                  </span>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Skeleton rows while a table loads (moved from the dashboard, PRP-WEB-1). */
export function TableSkeleton() {
  return (
    <div className="overflow-hidden rounded-[var(--r-lg)] border border-[var(--bd-subtle)] bg-[var(--bg-2)]">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex h-[var(--row-h)] items-center gap-3 border-b border-[var(--bd-subtle)] px-3">
          <Skeleton className="h-3.5 w-[140px]" />
          <Skeleton className="h-3.5 w-[180px]" />
          <Skeleton className="h-3.5 w-[80px]" />
          <div className="flex-1" />
          <Skeleton className="h-5 w-[60px]" />
          <Skeleton className="h-6 w-6 rounded-full" />
        </div>
      ))}
    </div>
  );
}
